// What the map paints. Each layer turns data the site already publishes into an
// ordered set of legend buckets plus a state -> bucket lookup.
//
// Two rules govern every layer here, and they are the reason this is a module with
// tests rather than a switch statement inside a component:
//
//  1. Nothing is invented. Every bucket is derived from a field that already exists
//     in src/lib/states.ts or src/lib/species.ts. Where a state has no usable value
//     it lands in the explicit "no data" bucket — never in the lowest one.
//  2. Colour is never the only cue. Every bucket carries a texture (drawn quietly by
//     src/components/map/tone-pattern.tsx, more lines for more reports) and a
//     written label, so the levels stay separable without colour vision and the
//     legend can say what each one means in a sentence.
import { STATES, type StateInfo } from "@/lib/states";
import { riskLabel, riskMeaning, RISK_LEVELS } from "@/lib/risk";
import { parsePeakSeason } from "@/lib/season";
import type { MapLayer } from "./search-params";

// Re-exported so existing imports keep working; the parser lives in season.ts.
export { parsePeakSeason, type ParsedSeason } from "@/lib/season";

/**
 * Position on the shared four-step ramp, or "none" for the absent-data neutral.
 * Layers reuse the audited risk tokens rather than inventing a palette each time.
 */
export type Tone = 0 | 1 | 2 | 3 | "none";

const TONE_FILL: Record<string, string> = {
  0: "var(--risk-low)",
  1: "var(--risk-emerging)",
  2: "var(--risk-established)",
  3: "var(--risk-high)",
  none: "var(--risk-none)",
};

const TONE_TEXT: Record<string, string> = {
  0: "var(--risk-low-foreground)",
  1: "var(--risk-emerging-foreground)",
  2: "var(--risk-established-foreground)",
  3: "var(--risk-high-foreground)",
  none: "var(--risk-none-foreground)",
};

/** The texture TonePattern draws over each tone's fill, named for what it looks like. */
const TONE_PATTERN: Record<string, string> = {
  0: "plain",
  1: "single-line",
  2: "double-line",
  3: "crosshatch",
  none: "dashes",
};

export interface LayerBucket {
  key: string;
  label: string;
  /** One sentence, shown inline in the legend. */
  meaning: string;
  tone: Tone;
  fill: string;
  foreground: string;
  patternId: string;
}

export interface LayerData {
  id: MapLayer;
  buckets: LayerBucket[];
  /** State abbreviation -> bucket key. A missing entry means the "no data" bucket. */
  byState: ReadonlyMap<string, string>;
  /** Optional per-state detail lines for the selection panel. */
  detail?: ReadonlyMap<string, string[]>;
  /** Shown under the legend when the layer needs a limitation stated plainly. */
  caveat?: string;
}

function bucket(key: string, label: string, meaning: string, tone: Tone): LayerBucket {
  return {
    key,
    label,
    meaning,
    tone,
    fill: TONE_FILL[String(tone)],
    foreground: TONE_TEXT[String(tone)],
    patternId: TONE_PATTERN[String(tone)],
  };
}

export const NO_DATA_BUCKET = bucket(
  "none",
  "Limited public data",
  "No usable public data for this area. That is not the same as low risk.",
  "none",
);

export function bucketFor(layer: LayerData, stateAbbr: string): LayerBucket {
  const key = layer.byState.get(stateAbbr);
  return layer.buckets.find((b) => b.key === key) ?? NO_DATA_BUCKET;
}

// --- Awareness ----------------------------------------------------------------

function awarenessLayer(): LayerData {
  return {
    id: "awareness",
    buckets: [
      ...RISK_LEVELS.map((level, i) =>
        bucket(level, riskLabel[level], riskMeaning[level], i as Tone),
      ),
      NO_DATA_BUCKET,
    ],
    byState: new Map(STATES.map((s) => [s.abbr, s.risk])),
  };
}

// --- Peak season --------------------------------------------------------------

const SEASON_BUCKETS = [
  bucket(
    "year-round",
    "Active year-round",
    "Ticks can be active in every month, with a spring-to-fall peak.",
    3,
  ),
  bucket("from-march", "Peaks from March", "Activity typically starts in March.", 2),
  bucket("from-april", "Peaks from April", "Activity typically starts in April.", 1),
  bucket("from-may", "Peaks from May", "Activity typically starts in May.", 0),
  bucket(
    "unspecified",
    "Spring to fall (no month-level data)",
    "We only have a broad spring-to-fall window for this state, so no start month is shown.",
    "none",
  ),
];

export function seasonBucketKey(state: StateInfo): string {
  const parsed = parsePeakSeason(state.peakSeason);
  if (parsed.yearRound) return "year-round";
  if (parsed.startMonth === null) return "unspecified";
  if (parsed.startMonth <= 3) return "from-march";
  if (parsed.startMonth === 4) return "from-april";
  return "from-may";
}

function seasonLayer(): LayerData {
  return {
    id: "season",
    buckets: SEASON_BUCKETS,
    byState: new Map(STATES.map((s) => [s.abbr, seasonBucketKey(s)])),
    detail: new Map(STATES.map((s) => [s.abbr, [s.peakSeason]])),
    caveat:
      "Peak season is the state's typical window, not a forecast. Ticks bite outside it, especially in a mild winter.",
  };
}

// --- Tick species -------------------------------------------------------------

export interface SpeciesRangeEntry {
  slug: string;
  commonName: string;
  range: string;
}

/**
 * Does this species' documented range description name this state?
 *
 * A deliberately strict, word-boundary read of the sourced prose in species.ts —
 * this reports what the source *says*, and nothing more. Range descriptions also use
 * regional shorthand ("the Southeastern states"), so a state that isn't named here
 * is not a state where the species is absent; the layer's caveat says so, and the
 * bucket is "not named", never "none present".
 */
export function statesNamedInRange(range: string, stateNames: readonly string[]): string[] {
  return stateNames.filter((name) =>
    new RegExp(`\\b${name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\b`, "i").test(range),
  );
}

const SPECIES_BUCKETS = [
  bucket("4+", "4 or more species", "Four or more profiled species name this state.", 3),
  bucket("3", "3 species", "Three profiled species name this state.", 2),
  bucket("2", "2 species", "Two profiled species name this state.", 1),
  bucket("1", "1 species", "One profiled species names this state.", 0),
  bucket(
    "0",
    "Not named",
    "No profiled species names this state in its range description. That is not the same as none being present.",
    "none",
  ),
];

export function speciesCountKey(count: number): string {
  if (count >= 4) return "4+";
  if (count >= 1) return String(count);
  return "0";
}

export function buildSpeciesLayer(species: readonly SpeciesRangeEntry[]): LayerData {
  const names = STATES.map((s) => s.name);
  const byState = new Map<string, string>();
  const detail = new Map<string, string[]>();
  const perState = new Map<string, string[]>();

  for (const sp of species) {
    for (const stateName of statesNamedInRange(sp.range, names)) {
      const list = perState.get(stateName) ?? [];
      list.push(sp.commonName);
      perState.set(stateName, list);
    }
  }
  for (const s of STATES) {
    const named = perState.get(s.name) ?? [];
    byState.set(s.abbr, speciesCountKey(named.length));
    detail.set(s.abbr, named.length ? named : ["No profiled species names this state by name."]);
  }
  return {
    id: "species",
    buckets: SPECIES_BUCKETS,
    byState,
    detail,
    caveat:
      "This counts the tick species whose published range description names the state. Range descriptions also use regional shorthand, so an unnamed state is not a tick-free state. Open a species page for its full range and sources.",
  };
}

/** Layers that need no extra data module, so the map can paint immediately. */
export function buildSyncLayer(id: Exclude<MapLayer, "species">): LayerData {
  if (id === "season") return seasonLayer();
  return awarenessLayer();
}

/**
 * The species layer needs src/lib/species.ts (~60KB of field-guide prose), which the
 * other three do not — so it is fetched only when someone actually selects it.
 */
export async function loadSpeciesLayer(): Promise<LayerData> {
  const { SPECIES } = await import("@/lib/species");
  return buildSpeciesLayer(
    SPECIES.map((s) => ({ slug: s.slug, commonName: s.commonName, range: s.range })),
  );
}
